import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { 
  Grid3X3, 
  Star, 
  Briefcase, 
  GraduationCap, 
  Film,
  PenLine,
  Palette,
  HardHat,
  ChevronDown,
  ExternalLink,
  Sparkles
} from "lucide-react";

/** Dropdown menu for switching between the 7 platform dashboards based on user roles. */
interface Platform {
  id: string;
  name: string;
  description: string;
  href: string;
  icon: React.ReactNode;
  roles: string[];
  badge?: string;
}

const platforms: Platform[] = [
  {
    id: "callback-zone",
    name: "Callback Zone",
    description: "Training & curriculum for actors",
    href: "/callback-zone",
    icon: <Star className="h-4 w-4" />,
    roles: ["actor", "parent"],
  },
  {
    id: "castings",
    name: "CastReady Castings",
    description: "Browse & submit to casting calls",
    href: "/castings",
    icon: <Film className="h-4 w-4" />,
    roles: ["actor", "casting_professional", "agent", "manager", "producer"],
    badge: "Full Platform",
  },
  {
    id: "coach",
    name: "CastReady Coach",
    description: "Manage students & lessons",
    href: "/coach",
    icon: <GraduationCap className="h-4 w-4" />,
    roles: ["coach"],
  },
  {
    id: "pro",
    name: "CastReady Pro",
    description: "Industry professional tools",
    href: "/pro",
    icon: <Briefcase className="h-4 w-4" />,
    roles: ["agent", "manager", "casting_professional", "producer", "studio", "coach"],
  },
  {
    id: "draftline",
    name: "CastReady Draftline",
    description: "Write, network, and track opportunities",
    href: "/draftline",
    icon: <PenLine className="h-4 w-4" />,
    roles: ["writer", "actor"],
  },
  {
    id: "hmu",
    name: "CastReady HMU",
    description: "Hair, makeup + styling services",
    href: "/hmu",
    icon: <Palette className="h-4 w-4" />,
    roles: ["hmu_artist", "actor", "parent"],
  },
  {
    id: "crew",
    name: "CastReady Crew",
    description: "Production crew marketplace",
    href: "/crew",
    icon: <HardHat className="h-4 w-4" />,
    roles: ["crew_member", "producer", "casting_professional"],
  },
];

export function PlatformSwitcher() {
  const { hasRole, user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  // Determine current platform based on route
  const getCurrentPlatform = () => {
    if (location.pathname.startsWith("/callback-zone")) return "callback-zone";
    if (location.pathname.startsWith("/castings")) return "castings";
    if (location.pathname.startsWith("/coach")) return "coach";
    if (location.pathname.startsWith("/pro")) return "pro";
    if (location.pathname.startsWith("/draftline")) return "draftline";
    if (location.pathname.startsWith("/hmu")) return "hmu";
    if (location.pathname.startsWith("/crew")) return "crew";
    return null;
  };

  const currentPlatformId = getCurrentPlatform();
  const currentPlatform = platforms.find(p => p.id === currentPlatformId);

  // Get accessible platforms based on user roles
  const accessiblePlatforms = platforms.filter(platform => {
    // Everyone can access Callback Zone, Castings, Draftline, and browse HMU/Crew
    if (platform.id === "callback-zone") return true;
    if (platform.id === "castings") return true;
    if (platform.id === "draftline") return true;
    if (platform.id === "hmu") return true;
    if (platform.id === "crew") return true;
    
    // Check if user has any of the required roles
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return platform.roles.some(role => hasRole(role as any));
  });

  if (!user) return null;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          {currentPlatform ? (
            <>
              {currentPlatform.icon}
              <span className="hidden sm:inline">{currentPlatform.name}</span>
            </>
          ) : (
            <>
              <Grid3X3 className="h-4 w-4" />
              <span className="hidden sm:inline">Platforms</span>
            </>
          )}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          CastReady Ecosystem
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {accessiblePlatforms.map((platform) => (
          <DropdownMenuItem key={platform.id} asChild className="cursor-pointer">
            <Link
              to={platform.href}
              className="flex items-start gap-3 p-2"
              onClick={() => setOpen(false)}
            >
              <div className="mt-0.5 text-primary">{platform.icon}</div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{platform.name}</span>
                  {platform.badge && (
                    <Badge variant="secondary" className="text-xs">
                      {platform.badge}
                    </Badge>
                  )}
                  {currentPlatformId === platform.id && (
                    <Badge variant="default" className="text-xs">
                      Current
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">{platform.description}</p>
              </div>
            </Link>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        
        <DropdownMenuItem asChild className="cursor-pointer">
          <Link to="/" className="flex items-center gap-2" onClick={() => setOpen(false)}>
            <ExternalLink className="h-4 w-4" />
            <span>Ecosystem Home</span>
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
